import React, { useCallback, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { ChecklistMajor } from "@shopify/polaris-icons"
import { Card, Modal, Tooltip } from "@shopify/polaris"
import FileQualityEntity from "services/entities/FileQualityEntity"
import Loading from "components/widgets/Loading"
import CardErrorAtom from "views/atoms/errors/CardErrorAtom"
import "stylesheets/common/text.scss"
import { useEntity } from "hooks/useAxios"
import PostTreatmentResultOrganism from "views/molecules/modals/files/PostTreatmentResultOrganism"
import EquipmentDetailOrganism from "views/atoms/charts/doughnuts/EquipmentDetailOrganism"
import MultiDoughnutOrganism from "views/atoms/charts/doughnuts/MultiDoughnutOrganism"
import MatchingBreakdownBarStackedChartAtom from "views/atoms/charts/doughnuts/MatchingBreakdownBarStackedChartAtom"
import { QUALITY_MODAL_CONFIGS } from "views/molecules/modals/files/QualityModalConfig"

const QualityFileModalMolecule = ({
  inventoryStatus,
  contextType = "assets",
}) => {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [activeModal, setActiveModal] = useState(false)
  const [fileQuality, setFileQuality] = useState({
    equipment_type_discarded_count: 0,
    final_total_count: 0,
    node_discarded_count: 0,
    equipment_types_discarded: {},
    equipment_types: {},
    matching_breakdown: {},
    nodes: {},
  })
  const fileQualityEntity = useEntity(FileQualityEntity)

  const toggleActiveModal = useCallback(() => setActiveModal(!activeModal), [
    activeModal,
  ])

  const getFileQuality = async () => {
    try {
      const fileQuality = await fileQualityEntity.getFileQuality(
        inventoryStatus.id
      )

      setFileQuality(fileQuality)
    } catch (err) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const getUnitTypeForContext = (contextType, sectionId = null) => {
    const config = QUALITY_MODAL_CONFIGS[contextType]

    if (sectionId) {
      const section = config.sections?.find((s) => s.id === sectionId)
      return section?.unitType || config.unitType
    }
  }

  useEffect(async () => {
    if (activeModal) {
      await getFileQuality()
    } else {
      setLoading(true)
      setError(false)
    }
  }, [activeModal])

  const activator = (
    <Tooltip
      content={t(`collect:quality.tooltip.message`)}
      dismissOnMouseOut
      light
    >
      <button onClick={toggleActiveModal} type="button">
        <ChecklistMajor />
      </button>
    </Tooltip>
  )

  const config = QUALITY_MODAL_CONFIGS[contextType]

  const componentMap = {
    PostTreatmentResultOrganism,
    EquipmentDetailOrganism,
    MultiDoughnutOrganism,
    MatchingBreakdownBarStackedChartAtom,
  }

  const matchingBreakDownLabelTranslationFunction = (() => {
    switch (contextType) {
      case "assets":
      case "datacenter":
        return (label) => t(`common:equipment_types.${label}`)
      default:
        return (label) => label
    }
  })()

  const renderSection = (section) => {
    const Component = componentMap[section.component]
    if (!Component) return null

    const unitType = getUnitTypeForContext(contextType, section.id)

    const props = {
      fileQuality,
      contextType,
      unitType,
      ...(section.props?.chartData && {
        chartData: fileQuality[section.props.chartData],
        labelTranslationFunction: matchingBreakDownLabelTranslationFunction,
        config: config,
      }),
      ...(section.props?.charts && {
        charts: section.props.charts,
      }),
    }

    return (
      <Modal.Section key={section.id}>
        <Card title={t(section.titleKey)}>
          {section.component === "PostTreatmentResultOrganism" ? (
            <Card.Section>
              <Component {...props} />
            </Card.Section>
          ) : (
            <Component {...props} />
          )}
        </Card>
      </Modal.Section>
    )
  }

  return (
    <Modal
      activator={activator}
      open={activeModal}
      onClose={toggleActiveModal}
      title={t(config?.titleKey || "collect:qualities.title")}
    >
      {error && !loading && (
        <Modal.Section>
          <CardErrorAtom />
        </Modal.Section>
      )}

      {!error && loading && (
        <Modal.Section>
          <Loading />
        </Modal.Section>
      )}

      {!error &&
        !loading &&
        config?.sections
          ?.filter(
            (section) =>
              typeof section.when !== "function" || section.when(fileQuality)
          )
          .map(renderSection)}
    </Modal>
  )
}

QualityFileModalMolecule.propTypes = {
  inventoryStatus: PropTypes.object.isRequired,
  contextType: PropTypes.string,
}

export default QualityFileModalMolecule
